import { Button } from '@invent/wl-ui-kit-next/ui';
import { Copy } from 'lucide-react';
import * as React from 'react';

/**
 * One value, readable and selectable, with a copy button that says what happened — an IdP
 * invitation's redeem link (`sign-in-card.tsx`), the participant's id in the hub's header and in
 * Identity & numbers, each Internal id staff paste into a search, and a response's id (the advisor
 * record page's F11, R4: IAM-08's "one copy button on the hub's header"). One component, so a
 * copy says the same thing wherever it is pressed.
 *
 * **The value is always on screen, as selectable text.** `navigator.clipboard` needs a secure
 * context and can be refused by permission policy, so a press that cannot write says "Copy it by
 * hand — the clipboard is not available here" beside a value that can be selected — a downgrade,
 * never a silent no-op, and never a value that exists only behind the button.
 *
 * Two layouts, one behaviour. **`label`** draws the label above the value and the note beside it —
 * the Sign-in card's redeem link, where the value stands alone. **Without it** the value, its button
 * and the note sit on one line, for a place that labels the value already (a `Fact`, a table
 * cell). **`name`** is the button's accessible name where "Copy" alone would not say what — two
 * copy buttons in one header, a row of Internal ids; the visible word stays "Copy", the name's
 * start.
 */
export function CopyableValue({
  label,
  value,
  name,
  compact = false
}: {
  label?: string;
  value: string;
  /** The value small and muted, the button an icon with its name — a record card's id, which is
   *  looked up now and then and copied rarely (SR-407). The value stays on screen either way. */
  compact?: boolean;
  /** The button's accessible name — "Copy the participant id". "Copy" where omitted. */
  name?: string;
}) {
  const [state, setState] = React.useState<'idle' | 'copied' | 'failed'>('idle');

  const copy = async () => {
    // Emptied first, so a second press's "Copied" is a change the live region says again.
    setState('idle');
    try {
      // Absent outside a secure context — a TypeError here, caught like a refusal.
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
  };

  const note = (
    <span aria-live="polite" className="text-xs text-muted-foreground">
      {state === 'copied' && 'Copied'}
      {state === 'failed' && 'Copy it by hand — the clipboard is not available here'}
    </span>
  );
  const button = compact ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={name ?? 'Copy'}
      className="h-6 px-1 text-muted-foreground"
      onClick={() => void copy()}
    >
      <Copy className="size-3.5" aria-hidden />
    </Button>
  ) : (
    <Button type="button" variant="outline" size="sm" aria-label={name} onClick={() => void copy()}>
      <Copy className="size-4" aria-hidden /> Copy
    </Button>
  );

  if (compact) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-1">
        <code className="text-xs break-all text-muted-foreground select-all">{value}</code>
        {button}
        {note}
      </span>
    );
  }

  if (label === undefined) {
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <code className="break-all select-all">{value}</code>
        {button}
        {note}
      </span>
    );
  }

  return (
    <div className="grid gap-1">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {note}
      </div>
      <div className="flex items-start gap-2">
        <code className="grow break-all rounded bg-card px-2 py-1 text-xs">{value}</code>
        {button}
      </div>
    </div>
  );
}
